import React, { useEffect, useState } from 'react';
import { siteConfig } from '../../data/mock';

// Tool logos - 9 logos arranged in 3-3-2-1 rows inside hourglass
const toolLogos = [
  // Row 1 (3 logos): Apollo, LinkedIn, Perplexity
  { name: 'Apollo.io', src: '/images/tools/Apollo IO.png', pos: { top: '10%', left: '33%' }, size: 'normal' },
  { name: 'LinkedIn', src: '/images/tools/LinkedIn.png', pos: { top: '10%', left: '50%' }, size: 'normal' },
  { name: 'Perplexity', src: '/images/tools/Perplexity_AI_logo.png', pos: { top: '10%', left: '67%' }, size: 'large' },
  // Row 2 (3 logos): Google, Lusha, WhatsApp - aligned on same line
  { name: 'G SERP API', src: 'https://customer-assets.emergentagent.com/job_talent-copilot-1/artifacts/98mm2fcq_New%20Google%20API.png', pos: { top: '18%', left: '32%' }, size: 'xlarge' },
  { name: 'Lusha', src: 'https://customer-assets.emergentagent.com/job_talent-copilot-1/artifacts/80ly569q_New%20Lusha%20logo.png', pos: { top: '18%', left: '50%' }, size: 'medium' },
  { name: 'WhatsApp', src: '/images/tools/WhatsApp.png', pos: { top: '18%', left: '68%' }, size: 'normal' },
  // Row 3 (2 logos): Supabase, n8n
  { name: 'Supabase', src: 'https://customer-assets.emergentagent.com/job_talent-copilot-1/artifacts/bkurlu19_New%20Supabase_Logo.png', pos: { top: '26%', left: '42%' }, size: 'medium' },
  { name: 'n8n', src: '/images/tools/N8n-logo.png', pos: { top: '26%', left: '58%' }, size: 'large' },
  // Row 4 (1 logo): Waalaxy
  { name: 'Waalaxy', src: '/images/tools/waalaxy-1.png', pos: { top: '33%', left: '50%' }, size: 'large' }
];

// Size classes for logos
const sizeClasses = {
  normal: 'w-8 h-8 sm:w-10 sm:h-10',
  medium: 'w-12 h-12 sm:w-16 sm:h-16',      // 1.5x normal
  large: 'w-10 h-10 sm:w-14 sm:h-14',
  xlarge: 'w-20 h-20 sm:w-28 sm:h-28'        // 3x normal
};

const PractitionersSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="heading-1 mb-4">
            Built by practitioners who <span className="gradient-text">hired & sold at scale</span>
          </h2>
          <p 
            className={`text-lg sm:text-xl md:text-2xl text-[var(--brand-teal-600)] tracking-wide transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            AI where it helps. Humans where it matters.
          </p>
        </div>

        {/* Hourglass Visualization */}
        <div className="relative max-w-2xl mx-auto">
          {/* Top Section - Instead of juggling */}
          <p className="text-center text-base sm:text-lg font-semibold text-[var(--text-primary)] mb-4">
            Instead of juggling multiple tools...
          </p>

          {/* Hourglass Container */}
          <div className="relative" style={{ minHeight: '450px' }}>
            {/* SVG Hourglass Shape */}
            <svg viewBox="0 0 400 500" className="w-full max-w-md mx-auto absolute inset-0" style={{ height: '100%' }}>
              {/* Hourglass outline */}
              <defs>
                <linearGradient id="hourglassGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="var(--brand-teal-100)" />
                  <stop offset="50%" stopColor="var(--brand-teal-200)" />
                  <stop offset="100%" stopColor="var(--brand-teal-100)" />
                </linearGradient>
              </defs>
              
              {/* Top trapezoid */}
              <path 
                d="M50,30 L350,30 L250,200 L150,200 Z" 
                fill="url(#hourglassGradient)"
                stroke="var(--brand-teal-300)"
                strokeWidth="2"
              />
              
              {/* Neck */}
              <path 
                d="M150,200 L250,200 L250,300 L150,300 Z" 
                fill="var(--brand-teal-50)"
                stroke="var(--brand-teal-300)"
                strokeWidth="2"
              />
              
              {/* Bottom trapezoid */}
              <path 
                d="M150,300 L250,300 L350,470 L50,470 Z" 
                fill="url(#hourglassGradient)"
                stroke="var(--brand-teal-300)"
                strokeWidth="2"
              />
              
              {/* Flow lines */}
              <path 
                d="M200,200 L200,300" 
                stroke="var(--brand-teal-400)"
                strokeWidth="3"
                strokeDasharray="5,5"
                className="animate-pulse"
              />
            </svg>

            {/* Tool Logos - Arranged 3-3-2-1 inside top half */}
            <div className="absolute inset-0 max-w-md mx-auto left-0 right-0">
              {toolLogos.map((tool) => (
                <div
                  key={tool.name}
                  className="absolute"
                  style={{ 
                    top: tool.pos.top, 
                    left: tool.pos.left,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <img 
                    src={tool.src} 
                    alt={tool.name}
                    className={`object-contain drop-shadow-md ${sizeClasses[tool.size]}`}
                  />
                </div>
              ))}
            </div>

            {/* Center - SuperHunt Logo (compact) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="bg-white rounded-xl shadow-lg p-2 border-2 border-[var(--brand-teal-400)]">
                <img 
                  src={siteConfig.logo} 
                  alt="SuperHunt" 
                  className="h-8 sm:h-10 w-auto"
                />
              </div>
            </div>

            {/* Output Audiences - Stacked inside bottom half */}
            <div className="absolute bottom-[8%] left-1/2 -translate-x-1/2 w-[65%] flex flex-col items-center gap-2">
              {/* Row 1: Search Partners */}
              <div className="px-3 py-1.5 rounded-full bg-white/90 border border-[var(--brand-teal-200)] text-[var(--brand-teal-700)] text-xs sm:text-sm font-medium shadow-sm">
                Search Partners
              </div>
              {/* Row 2: TA Leaders */}
              <div className="px-3 py-1.5 rounded-full bg-white/90 border border-[var(--brand-teal-200)] text-[var(--brand-teal-700)] text-xs sm:text-sm font-medium shadow-sm">
                TA Leaders
              </div>
              {/* Row 3: Sales Pros & Founders side by side */}
              <div className="flex gap-2">
                <div className="px-3 py-1.5 rounded-full bg-white/90 border border-[var(--brand-teal-200)] text-[var(--brand-teal-700)] text-xs sm:text-sm font-medium shadow-sm">
                  Sales Pros
                </div>
                <div className="px-3 py-1.5 rounded-full bg-white/90 border border-[var(--brand-teal-200)] text-[var(--brand-teal-700)] text-xs sm:text-sm font-medium shadow-sm">
                  Founders
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Text */}
          <p className="text-center text-base sm:text-lg font-semibold text-[var(--text-primary)] mt-6">
            They all just use <span className="text-[var(--brand-teal-600)]">SuperHunt.ai</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PractitionersSection;
